import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import ModalStatistiques from './ModalStatistiques';
import ModalPlanning from './ModalPlanning';
import ModalHabilitationsOperateur from './ModalHabilitationsOperateur';
import JsBarcode from 'jsbarcode';
import { calculerDureeCreneauMinutes, formatHeuresMinutes } from '../utils/formatDuree';

function GestionOperateurs() {
  const [operateurs, setOperateurs] = useState([]);
  const [profils, setProfils] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingOperateur, setEditingOperateur] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showHabilitationsModal, setShowHabilitationsModal] = useState(false);
  const [selectedOperateur, setSelectedOperateur] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    identifiant: '',
    mot_de_passe: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    anciennete_annees: 0,
    anciennete_mois: 0,
    profil_id: '',
    code_barre: '',
    type_contrat: 'employe',
    inclus_dans_planning: null
  });

  useEffect(() => {
    loadOperateurs();
    loadProfils();
  }, []);

  const loadOperateurs = async () => {
    const { data, error } = await supabase
      .from('operateurs')
      .select(`*, profils (nom, inclus_dans_planning), planning_operateurs (jour_semaine, heure_debut, heure_fin)`)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Erreur:', error);
    } else {
      setOperateurs(data || []);
    }
  };

  const calculerHeuresHebdo = (operateur) => {
    if (!operateur.planning_operateurs || operateur.planning_operateurs.length === 0) return 0;
    return operateur.planning_operateurs.reduce((sum, creneau) => {
      return sum + calculerDureeCreneauMinutes(
        creneau.heure_debut.substring(0, 5),
        creneau.heure_fin.substring(0, 5)
      );
    }, 0);
  };

  const loadProfils = async () => {
    const { data, error } = await supabase
      .from('profils')
      .select('*')
      .order('nom', { ascending: true });
    
    if (error) {
      console.error('Erreur:', error);
    } else {
      setProfils(data || []);
    }
  };

  const generateCodeBarre = () => {
    // Génère un code plus court : OPR suivi de 8 caractères
    // Alphabet compatible QWERTY/AZERTY (sans A, M, Q, W, Z qui changent entre les deux layouts)
    // Également sans O, I pour éviter confusion avec 0, 1
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'OPR';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation : le profil est obligatoire
    if (!formData.profil_id) {
      alert('⚠️ Le profil est obligatoire. Veuillez sélectionner un profil.');
      return;
    }
    
    if (editingOperateur) {
      // Modification d'un opérateur existant
      const { mot_de_passe, ...updateData } = formData;
      
      // Mise à jour des informations de base (incluant l'identifiant)
      const { error } = await supabase
        .from('operateurs')
        .update(updateData)
        .eq('id', editingOperateur.id);
      
      if (error) {
        console.error('Erreur mise à jour:', error);
        alert('Erreur lors de la mise à jour');
        return;
      }
      
      // Si un nouveau mot de passe est fourni, le mettre à jour
      if (mot_de_passe && mot_de_passe.trim() !== '') {
        const { error: pwdError } = await supabase.rpc('modifier_mot_de_passe', {
          p_operateur_id: editingOperateur.id,
          p_nouveau_mot_de_passe: mot_de_passe
        });
        
        if (pwdError) {
          console.error('Erreur mot de passe:', pwdError);
          alert('Erreur lors de la modification du mot de passe');
          return;
        }
      }
      
      loadOperateurs();
      closeModal();
    } else {
      // Création d'un nouvel opérateur
      const codeBarre = generateCodeBarre();
      
      const { data, error } = await supabase.rpc('creer_operateur', {
        p_nom: formData.nom,
        p_prenom: formData.prenom,
        p_identifiant: formData.identifiant,
        p_mot_de_passe: formData.mot_de_passe,
        p_profil_id: formData.profil_id || null,
        p_code_barre: codeBarre,
        p_email: formData.email || null,
        p_telephone: formData.telephone || null,
        p_adresse: formData.adresse || null,
        p_code_postal: formData.code_postal || null,
        p_ville: formData.ville || null,
        p_anciennete_annees: formData.anciennete_annees || 0,
        p_anciennete_mois: formData.anciennete_mois || 0
      });
      
      if (error) {
        console.error('Erreur création:', error);
        alert('Erreur lors de la création: ' + error.message);
        return;
      }

      // Si inclus_dans_planning a été modifié (pas null), mettre à jour
      if (formData.inclus_dans_planning !== null && data) {
        await supabase
          .from('operateurs')
          .update({ inclus_dans_planning: formData.inclus_dans_planning })
          .eq('id', data);
      }
      
      loadOperateurs();
      closeModal();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cet opérateur ?')) {
      const { error } = await supabase
        .from('operateurs')
        .delete()
        .eq('id', id);
      
      if (!error) {
        loadOperateurs();
      }
    }
  };

  const openModal = (operateur = null) => {
    if (operateur) {
      setEditingOperateur(operateur);
      setFormData({
        nom: operateur.nom,
        prenom: operateur.prenom,
        identifiant: operateur.identifiant || '',
        mot_de_passe: '', // Toujours vide pour la modification
        email: operateur.email || '',
        telephone: operateur.telephone || '',
        adresse: operateur.adresse || '',
        code_postal: operateur.code_postal || '',
        ville: operateur.ville || '',
        anciennete_annees: operateur.anciennete_annees || 0,
        anciennete_mois: operateur.anciennete_mois || 0,
        profil_id: operateur.profil_id || '',
        code_barre: operateur.code_barre || '',
        type_contrat: operateur.type_contrat || 'employe',
        inclus_dans_planning: operateur.inclus_dans_planning
      });
    } else {
      setEditingOperateur(null);
      setFormData({
        nom: '',
        prenom: '',
        identifiant: '',
        mot_de_passe: '',
        email: '',
        telephone: '',
        adresse: '',
        code_postal: '',
        ville: '',
        anciennete_annees: 0,
        anciennete_mois: 0,
        profil_id: '',
        code_barre: '',
        type_contrat: 'employe',
        inclus_dans_planning: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOperateur(null);
  };

  const handlePrint = (operateur) => {
    // Créer un canvas temporaire pour générer le code-barre
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, operateur.code_barre, {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=200');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${operateur.prenom} ${operateur.nom}</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                width: 89mm;
                height: 36mm;
                margin: 0;
                padding: 2mm 2mm;
                overflow: hidden;
              }
              
              .label-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              
              .barcode-section {
                text-align: center;
                margin-bottom: 0.5mm;
              }
              
              .barcode-image {
                width: 70mm;
                height: auto;
                max-height: 14mm;
                margin: 0 auto 0.5mm auto;
                display: block;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .operator-info {
                text-align: center;
              }
              
              .name {
                font-size: 9pt;
                font-weight: bold;
                color: #000;
                margin-bottom: 0.5mm;
              }
              
              .profil {
                font-size: 7pt;
                color: #333;
                font-weight: 600;
              }
              
              @media print {
                body {
                  width: 89mm;
                  height: 36mm;
                  padding: 2mm 2mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="barcode-section">
                <img src="${barcodeDataUrl}" class="barcode-image" alt="Barcode"/>
                <div class="code-text">${operateur.code_barre}</div>
              </div>
              
              <div class="operator-info">
                <div class="name">${operateur.prenom} ${operateur.nom}</div>
                <div class="profil">${operateur.profils?.nom || 'Sans profil'}</div>
              </div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 250);
              };
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur lors de la génération du code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
        <div>
          <h1 style={{ color: '#212529', fontSize: '32px', fontWeight: '600', margin: 0, marginBottom: '8px' }}>
            Gestion des Opérateurs
          </h1>
          <p style={{ color: '#6c757d', fontSize: '15px', margin: 0 }}>
            Gérez votre équipe et leurs informations
          </p>
        </div>
        <button onClick={() => openModal()} style={{ padding: '14px 32px', backgroundColor: '#1aabf9', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', boxShadow: '0 4px 15px rgba(26, 171, 249, 0.3)' }}>
          + Nouvel Opérateur
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '24px' }}>
        {operateurs.map(op => (
          <div key={op.id} style={{ backgroundColor: '#ffffff', borderRadius: '15px', padding: '28px', border: '1px solid #e0e5ea', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#212529', margin: '0 0 5px 0', fontSize: '20px', fontWeight: '600' }}>
                {op.prenom} {op.nom}
              </h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ backgroundColor: '#e3f2fd', color: '#1aabf9', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                  {op.profils?.nom || 'Sans profil'}
                </span>
                {op.type_contrat === 'sous_traitant' ? (
                  <span style={{ backgroundColor: '#fff3cd', color: '#ff8c00', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                    🤝 Sous-traitant
                  </span>
                ) : (
                  <span style={{ backgroundColor: '#d4edda', color: '#28a745', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                    👷 Employé
                  </span>
                )}
                {(() => {
                  const effectif = op.inclus_dans_planning !== null ? op.inclus_dans_planning : (op.profils?.inclus_dans_planning || false);
                  return effectif ? (
                    <span style={{ backgroundColor: '#d1fae5', color: '#059669', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                      📅 Planning{op.inclus_dans_planning !== null ? ' (forcé)' : ''}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #dee2e6' }}>
              <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '5px', fontWeight: '600', textTransform: 'uppercase' }}>
                Code-barre
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '16px', color: '#212529', fontWeight: '700', letterSpacing: '1px' }}>
                {op.code_barre}
              </div>
            </div>

            {(() => {
              const minutesHebdo = calculerHeuresHebdo(op);
              return minutesHebdo > 0 ? (
                <div style={{ marginBottom: '20px', padding: '12px 15px', backgroundColor: '#e8f5e9', borderRadius: '10px', border: '1px solid #c8e6c9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: '600' }}>📅 Heures / semaine</span>
                  <span style={{ fontSize: '16px', color: '#2e7d32', fontWeight: '700' }}>{formatHeuresMinutes(minutesHebdo)}</span>
                </div>
              ) : (
                <div style={{ marginBottom: '20px', padding: '12px 15px', backgroundColor: '#fff3cd', borderRadius: '10px', border: '1px solid #ffc107', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#856404', fontStyle: 'italic' }}>⚠️ Aucun créneau planning défini</span>
                </div>
              );
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px', fontSize: '13px' }}>
              {op.telephone && (
                <div>
                  <div style={{ color: '#6c757d', marginBottom: '3px' }}>📞 Téléphone</div>
                  <div style={{ color: '#212529', fontWeight: '500' }}>{op.telephone}</div>
                </div>
              )}
              {op.mail && (
                <div>
                  <div style={{ color: '#6c757d', marginBottom: '3px' }}>✉️ Email</div>
                  <div style={{ color: '#212529', fontWeight: '500', fontSize: '12px' }}>{op.mail}</div>
                </div>
              )}
              <div>
                <div style={{ color: '#6c757d', marginBottom: '3px' }}>⏱️ Ancienneté</div>
                <div style={{ color: '#212529', fontWeight: '500' }}>
                  {op.anciennete_annees} an{op.anciennete_annees > 1 ? 's' : ''} {op.anciennete_mois} mois
                </div>
              </div>
              {(op.ville || op.code_postal) && (
                <div>
                  <div style={{ color: '#6c757d', marginBottom: '3px' }}>📍 Localisation</div>
                  <div style={{ color: '#212529', fontWeight: '500' }}>{op.code_postal} {op.ville}</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', paddingTop: '20px', borderTop: '1px solid #e0e5ea', flexWrap: 'wrap' }}>
              <button onClick={() => { setSelectedOperateur(op); setShowStatsModal(true); }} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: '#28a745', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                📊 Stats
              </button>
              <button onClick={() => { setSelectedOperateur(op); setShowPlanningModal(true); }} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: '#6f42c1', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                📅 Planning
              </button>
              <button onClick={() => { setSelectedOperateur(op); setShowHabilitationsModal(true); }} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: '#ff5722', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                🔐 Habilitations
              </button>
              <button onClick={() => handlePrint(op)} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: '#ff9800', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                🖨️ Imprimer
              </button>
              <button onClick={() => openModal(op)} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: '#1aabf9', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                ✏️ Modifier
              </button>
              <button onClick={() => handleDelete(op.id)} style={{ flex: '1 1 auto', padding: '10px 16px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                🗑️ Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>

      {operateurs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#6c757d', fontSize: '15px', backgroundColor: '#ffffff', borderRadius: '15px', border: '2px dashed #dee2e6' }}>
          Aucun opérateur enregistré.
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '20px', padding: '40px', width: '700px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto', border: '2px solid #1aabf9' }}>
            <h2 style={{ color: '#212529', marginTop: 0, marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>
              {editingOperateur ? 'Modifier l\'opérateur' : 'Nouvel opérateur'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Prénom *</label>
                  <input type="text" value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})} required style={{ width: '91%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Nom *</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} required style={{ width: '91%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Identifiant *
                </label>
                <input 
                  type="text" 
                  value={formData.identifiant} 
                  onChange={(e) => setFormData({...formData, identifiant: e.target.value})} 
                  required
                  style={{ 
                    width: '96%', 
                    padding: '12px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px', 
                    fontSize: '14px'
                  }} 
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Mot de passe {!editingOperateur && '*'}
                </label>
                <input 
                  type="password" 
                  value={formData.mot_de_passe} 
                  onChange={(e) => setFormData({...formData, mot_de_passe: e.target.value})} 
                  required={!editingOperateur}
                  placeholder={editingOperateur ? 'Laisser vide pour ne pas modifier' : ''}
                  style={{ width: '96%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} 
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Profil <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select 
                  value={formData.profil_id} 
                  onChange={(e) => setFormData({...formData, profil_id: e.target.value})} 
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    backgroundColor: '#f8f9fa', 
                    border: formData.profil_id ? '1px solid #dee2e6' : '2px solid #dc3545', 
                    borderRadius: '8px', 
                    fontSize: '14px', 
                    cursor: 'pointer' 
                  }}
                >
                  <option value="">Sélectionner un profil (obligatoire)</option>
                  {profils.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  Type de contrat <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <select 
                  value={formData.type_contrat} 
                  onChange={(e) => setFormData({...formData, type_contrat: e.target.value})} 
                  required
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px', 
                    fontSize: '14px', 
                    cursor: 'pointer' 
                  }}
                >
                  <option value="employe">👷 Employé</option>
                  <option value="sous_traitant">🤝 Sous-traitant</option>
                </select>
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  Le chiffre d'affaires sera visible uniquement pour les sous-traitants
                </small>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                  📅 Planning de production
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: null, label: '🔗 Hérite du profil', color: '#6c757d', bg: '#f8f9fa', border: '#dee2e6' },
                    { value: true, label: '✅ Inclus', color: '#059669', bg: '#d1fae5', border: '#059669' },
                    { value: false, label: '⊘ Exclu', color: '#dc3545', bg: '#fee2e2', border: '#dc3545' }
                  ].map(option => (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => setFormData({...formData, inclus_dans_planning: option.value})}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        backgroundColor: formData.inclus_dans_planning === option.value ? option.bg : '#ffffff',
                        color: formData.inclus_dans_planning === option.value ? option.color : '#6c757d',
                        border: formData.inclus_dans_planning === option.value ? `2px solid ${option.border}` : '1px solid #dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: formData.inclus_dans_planning === option.value ? '600' : '400',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  "Hérite du profil" utilise le réglage du profil. Forcer inclus/exclu pour les cas particuliers (mi-temps, formation...).
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Téléphone</label>
                  <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} style={{ width: '91%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} style={{ width: '91%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Adresse</label>
                <input type="text" value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})} style={{ width: '96%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Code postal</label>
                  <input type="text" value={formData.code_postal} onChange={(e) => setFormData({...formData, code_postal: e.target.value})} style={{ width: '91%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', color: '#495057', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Ville</label>
                  <input type="text" value={formData.ville} onChange={(e) => setFormData({...formData, ville: e.target.value})} style={{ width: '94%', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#495057', marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>Ancienneté</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <input type="number" value={formData.anciennete_annees} onChange={(e) => setFormData({...formData, anciennete_annees: parseInt(e.target.value) || 0})} min="0" placeholder="Années" style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                  <input type="number" value={formData.anciennete_mois} onChange={(e) => setFormData({...formData, anciennete_mois: parseInt(e.target.value) || 0})} min="0" max="11" placeholder="Mois" style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeModal} style={{ padding: '12px 28px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                  Annuler
                </button>
                <button type="submit" style={{ padding: '12px 28px', backgroundColor: '#1aabf9', color: '#ffffff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                  {editingOperateur ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showStatsModal && selectedOperateur && (
        <ModalStatistiques
          operateur={selectedOperateur}
          onClose={() => { setShowStatsModal(false); setSelectedOperateur(null); }}
        />
      )}

      {showPlanningModal && selectedOperateur && (
        <ModalPlanning
          operateur={selectedOperateur}
          onClose={() => { setShowPlanningModal(false); setSelectedOperateur(null); loadOperateurs(); }}
        />
      )}

      {showHabilitationsModal && selectedOperateur && (
        <ModalHabilitationsOperateur
          operateur={selectedOperateur}
          onClose={() => { setShowHabilitationsModal(false); setSelectedOperateur(null); }}
          onUpdate={loadOperateurs}
        />
      )}
    </div>
  );
}

export default GestionOperateurs;